/**
 * run_template Tool - Create job from template
 *
 * Templates let you run predefined test configurations
 * without writing full descriptions every time.
 */

import type { Tool } from '@modelcontextprotocol/sdk/types.js';
import type { CreateTestJobRequest } from '@runhuman/shared';
import type { JobClient } from '../clients/job-client.js';

export const runTemplateToolDefinition: Tool = {
  name: 'run_template',
  description: `Create a QA test job from a template.

Templates are reusable test configurations that define default URL, test instructions, output schema, duration settings, and more.

How it works:
1. List available templates with list_templates
2. Choose a template by name
3. Call run_template with the template name
4. Optionally override template values (url, description, etc.)

Any parameters you provide will override the template's defaults.
This tool creates the job immediately and returns the jobId.
Use the wait tool to poll for results: wait(jobId: "...")`,
  inputSchema: {
    type: 'object',
    properties: {
      projectId: {
        type: 'string',
        description: 'Project ID (required). The project this job belongs to.',
      },
      template: {
        type: 'string',
        description: 'Template name (get from list_templates)',
      },
      url: {
        type: 'string',
        description: 'URL to test (overrides template default)',
      },
      description: {
        type: 'string',
        description: 'Additional test instructions (appended to template description)',
      },
      outputSchema: {
        type: 'object',
        description: 'Custom output schema (overrides template)',
      },
      targetDurationMinutes: {
        type: 'number',
        description: 'Target test duration in minutes (overrides template)',
        minimum: 1,
        maximum: 60,
      },
      maxExtensionMinutes: {
        type: 'number',
        description: 'Total overtime budget in minutes on top of base duration (overrides template). Default: 15. Set to 0 to disable.',
      },
      deviceClass: {
        type: 'string',
        enum: ['desktop', 'mobile', 'both'],
        description: 'Device class hint for the claim surface and viewport (overrides the template). Required-Devices take precedence — see create_job for the full resolution chain.',
      },
      githubRepos: {
        type: 'array',
        items: { type: 'string' },
        description: 'GitHub repos in format "owner/repo" each (overrides template)',
      },
      githubRepo: {
        type: 'string',
        description: 'Single GitHub repo in "owner/repo" format (overrides template). Use githubRepos for multiple.',
      },
      additionalValidationInstructions: {
        type: 'string',
        description: 'Extra validation rules (appended to template)',
      },
    },
    required: ['projectId', 'template'],
  },
};

export async function handleRunTemplate(
  args: CreateTestJobRequest & { template: string },
  jobClient: JobClient
): Promise<{ content: Array<{ type: 'text'; text: string }>; isError?: boolean }> {
  try {
    const result = await jobClient.createJob(args);

    const overrides: string[] = [];
    if (args.url) overrides.push(`  URL: ${args.url}`);
    if (args.description) overrides.push(`  Description: Added`);
    if (args.outputSchema) overrides.push(`  Output Schema: Custom`);
    if (args.targetDurationMinutes) overrides.push(`  Duration: ${args.targetDurationMinutes}min`);
    if (args.deviceClass) overrides.push(`  Device: ${args.deviceClass}`);
    if (args.githubRepos) overrides.push(`  GitHub: ${args.githubRepos.join(', ')}`);
    if (args.additionalValidationInstructions) overrides.push(`  Extra Validation: Added`);

    const overridesText = overrides.length > 0
      ? `\n\nOverrides Applied:\n${overrides.join('\n')}`
      : '';

    return {
      content: [{
        type: 'text',
        text: `Test Job Created from Template!

Template: ${args.template}
Job ID: ${result.jobId}${overridesText}

Next Steps:
1. Wait for results: wait(jobId: "${result.jobId}")`,
      }],
    };
  } catch (error) {
    const err = error as Error & { status?: number };

    if (err.status === 404) {
      return {
        content: [{
          type: 'text',
          text: `Template Not Found

Template: ${args.template}

Use list_templates to see available templates.
Templates are project-specific - make sure you're using the correct project.`,
        }],
        isError: true,
      };
    }

    return {
      content: [{
        type: 'text',
        text: `Failed to create job from template

Template: ${args.template}
Error: ${err.message}
${err.status ? `Status: ${err.status}` : ''}`,
      }],
      isError: true,
    };
  }
}
